// src/moment/components/WordOfTheDay/WordOfTheDayWrapper.tsx

import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WordOfTheDay, { WordOfTheDayProps } from './WordOfTheDay';
import { useWidgetData } from '../../hooks/useWidgetData';
import {
  FeedQueryResult,
  LightboxItem,
  LightboxLanguageText,
} from '../../net/softbox-types';
import { getSSRTranslation } from '../../../i18n';
import '../WidgetCard/WidgetCard.scss'; // Re-using styles from WidgetCard
import WordOfTheDaySkeleton from './WordOfTheDaySkeleton';

// Define the shape of a word item from the API, extending the base LightboxItem
interface WordItem extends LightboxItem {
  word?: {
    word: LightboxLanguageText;
    definition: LightboxLanguageText;
    context?: LightboxLanguageText
    wordType?: string
    pronunciation?: string
  };
}

// Define props for the wrapper, allowing feedType to be customized
interface WordOfTheDayWrapperProps {
  feedType?: string;
  dynamicFeed?: boolean
  showHeader?: boolean
}

const WordOfTheDayWrapper: FC<WordOfTheDayWrapperProps> = ({
                                                             feedType = 'words',
                                                             dynamicFeed = false,
                                                             showHeader = true
                                                           }) => {
  const { i18n, t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0); // Tracks the currently displayed word

  // Processor function to transform the generic feed data into WordOfTheDayProps
  const processWordData = useCallback(
    (feedData: FeedQueryResult): WordOfTheDayProps[] => {
      if (!feedData?.news?.items) {
        return [];
      }

      return feedData.news.items
        .map((item) => {
          const wordItem = item as WordItem;
          const word = wordItem.word?.word
            ? getSSRTranslation(wordItem.word.word, i18n)
            : getSSRTranslation(item.title, i18n);
          const definition = wordItem.word?.definition
            ? getSSRTranslation(wordItem.word.definition, i18n)
            : '';

          const context = wordItem.word?.context ? getSSRTranslation(wordItem.word.context, i18n) : undefined
          const wordType = wordItem.word?.wordType
          const pronunciation = wordItem.word?.pronunciation

          if (!word || !definition) {
            return null;
          }

          return {
            header: t('wordOfTheDay.header', 'Word of the Day'),
            contextTitle: t('wordOfTheDay.inContext', 'In Context:'),
            word,
            definition,
            pronunciation,
            wordType,
            context,
            showHeader
          };
        })
        .filter((word): word is WordOfTheDayProps => word !== null);
    },
    [i18n, t]
  );

  // Use the generic useWidgetData hook
  const {
    data: wordList,
    loading,
    error,
    refetch,
  } = useWidgetData<WordOfTheDayProps>(feedType, processWordData, dynamicFeed);

  // Derive the current word from the fetched data and the current index
  const currentWord = wordList?.[currentIndex];

  const handleNextWord = useCallback(() => {
    debugger
    if (wordList.length === 0) {
      if (error) refetch();
      return;
    }
    // Cycle to the next word, or back to the start if at the end
    const nextIndex = (currentIndex + 1) % wordList.length;
    setCurrentIndex(nextIndex);
    // Reset to the first word if the data changes to prevent out-of-bounds errors
    if (currentIndex >= wordList.length) setCurrentIndex(0);
  }, [currentIndex, wordList, error, refetch]);

  // --- Render Logic ---

  // 1. Loading state
  if (loading) {
    return <WordOfTheDaySkeleton />;
  }

  // 2. Error state
  if (error) {
    return (
      <div onClick={() => refetch()} style={{ cursor: 'pointer', margin: 0, padding: 0 }}>
        Error: {error}. Click to retry.
      </div>
    );
  }

  // 3. Success state (with or without data)
  return (
    <div onClick={handleNextWord} style={{ cursor: 'pointer', margin: 0, padding: 0 }}>
      {currentWord ? <WordOfTheDay {...currentWord} /> : <div>No words available. Click to retry.</div>}
    </div>
  );
};

export default WordOfTheDayWrapper;
